import { FC, FormEvent } from 'react'

import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

// import clsx from 'clsx'
import { AVAILABLE_ID_PASSPORT, MAX_YEARS_DURATIONS } from 'shared/constants'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { REGEX_EMAIL_NUMBER, REGEX_NAME } from 'shared/constants/regex'
import { addDate, dateFormat } from 'shared/lib/dateFormat'
// import { CheckBox } from 'shared/ui/check-box'
import { DatePickerWithLabel } from 'shared/ui/date-picker-label'
import { GenderSelect } from 'shared/ui/gender-select'
import { InputWithLabel } from 'shared/ui/input-label'
import { Paper } from 'shared/ui/paper'
import { SelectWithLabel } from 'shared/ui/select-label'

import { CloseCircle } from '../../model'

import s from './styles.module.scss'

interface Citizenships {
  value: string
  name: string
}

interface Options {
  name: string
  value: string
}

export interface FormCloseCircle {
  form: CloseCircle & { hasMiddle?: boolean }
}

export interface Props {
  register?: UseFormRegister<FormCloseCircle>
  control?: Control<FormCloseCircle>
  errors?: FieldErrors<FormCloseCircle>
  setValue: UseFormSetValue<FormCloseCircle>
  options: {
    showLabel?: boolean
    citizenships?: Citizenships[]
    typesDocuments?: Options[]
  }
}

export const UserForm: FC<Props> = ({
  register,
  control,
  options,
  errors,
  setValue,
}) => {
  const { t } = useTranslation()

  const { citizenships, typesDocuments, showLabel } = options

  const maxDateExpiration = addDate({
    count: MAX_YEARS_DURATIONS,
    type: 'year',
  })

  const onChangeInput = (event: FormEvent<HTMLInputElement>) => {
    return event?.currentTarget?.value?.toUpperCase().replace(REGEX_NAME, '')
  }

  const onChangeInputNumber = (event: FormEvent<HTMLInputElement>) => {
    return event?.currentTarget?.value
      ?.toUpperCase()
      .replace(REGEX_EMAIL_NUMBER, '')
  }

  // const hasMiddleName = useWatch({ control, name: 'form.hasMiddle' })
  const citizenship = useWatch({ control, name: 'form.citizenship' })

  const filteredDocuments = AVAILABLE_ID_PASSPORT.includes(citizenship)
    ? typesDocuments
    : typesDocuments?.filter((item) => item.value === 'intpassport')

  return (
    <Paper>
      <div className={s.wrapper}>
        {showLabel && (
          <InputWithLabel
            error={errors?.form?.label}
            label={t('write_it_down_as')}
            {...register?.('form.label', {
              required: true,
              minLength: 2,
              maxLength: 30,
            })}
          />
        )}

        <Controller
          control={control}
          name="form.last_name"
          rules={{ required: true, minLength: 2, maxLength: 30 }}
          render={({ field }) => (
            <InputWithLabel
              ref={field.ref}
              value={field?.value}
              label={t('form.last_name')}
              error={errors?.form?.last_name}
              onChange={(event) => field?.onChange(onChangeInput(event))}
            />
          )}
        />

        <Controller
          control={control}
          name="form.first_name"
          rules={{ required: true, minLength: 2, maxLength: 30 }}
          render={({ field }) => (
            <InputWithLabel
              ref={field.ref}
              value={field?.value}
              label={t('form.first_name')}
              error={errors?.form?.first_name}
              onChange={(event) => field?.onChange(onChangeInput(event))}
            />
          )}
        />

        <Controller
          name="form.gender"
          control={control}
          rules={{ required: t('select_gender') }}
          render={({ field }) => (
            <GenderSelect
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              error={errors?.form?.gender}
            />
          )}
        />

        <Controller
          name="form.date_of_birth"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <DatePickerWithLabel
              {...field}
              error={errors?.form?.date_of_birth}
              label={t('birth_day')}
              value={field.value ? new Date(field.value) : undefined}
              onChange={(date) => {
                field.onChange(
                  dateFormat(date, DATE_FORMAT_LIST.YEAR_FULL_DATE),
                )
              }}
            />
          )}
        />

        <Controller
          name="form.citizenship"
          control={control}
          rules={{ required: true }}
          render={({ field }) => {
            return (
              <SelectWithLabel
                {...field}
                ref={field?.ref}
                value={field?.value}
                options={citizenships}
                label={t('citizenship')}
                onChange={(option) => {
                  field.onChange(option.value)

                  setValue('form.document_type', '')
                  setValue('form.document_number', '')
                }}
                error={errors?.form?.citizenship}
              />
            )
          }}
        />

        <Controller
          name="form.document_type"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <SelectWithLabel
              key={field.value}
              {...field}
              ref={field?.ref}
              value={field.value}
              options={filteredDocuments}
              label={t('type_passport')}
              onChange={(option) => field.onChange(option.value)}
              error={errors?.form?.document_type}
            />
          )}
        />

        <Controller
          control={control}
          name="form.document_number"
          rules={{ required: true, minLength: 2, maxLength: 30 }}
          render={({ field }) => (
            <InputWithLabel
              ref={field.ref}
              value={field?.value}
              label={t('serial_documents')}
              error={errors?.form?.document_number}
              onChange={(event) => field?.onChange(onChangeInputNumber(event))}
            />
          )}
        />

        <Controller
          name="form.document_expire"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <DatePickerWithLabel
              {...field}
              minDate={new Date()}
              maxDate={maxDateExpiration}
              rangeYear={MAX_YEARS_DURATIONS}
              label={t('expiration_date')}
              value={field.value ? new Date(field.value) : undefined}
              error={errors?.form?.document_expire}
              onChange={(date) => {
                field.onChange(
                  dateFormat(date, DATE_FORMAT_LIST.YEAR_FULL_DATE),
                )
              }}
            />
          )}
        />

        {/* <Controller
          control={control}
          name="form.hasMiddle"
          render={({ field }) => (
            <CheckBox
              isChecked={!!field?.value}
              title={t('does_middle_name')}
              onClick={(value) => {
                field.onChange(value)
              }}
            />
          )}
        />

        <Controller
          control={control}
          name="form.middle_name"
          rules={{ required: hasMiddleName, minLength: 2, maxLength: 30 }}
          render={({ field }) => (
            <div className={clsx(!hasMiddleName && s.disabledField)}>
              <Input
                disabled={!hasMiddleName}
                ref={field.ref}
                value={field?.value}
                placeholder={t('form.middle_name')}
                error={errors?.form?.middle_name}
                onChange={(event) => field?.onChange(onChangeInput(event))}
              />
            </div>
          )}
        /> */}

        <div className={s.selects}></div>
      </div>
    </Paper>
  )
}
